<template>
    <div class="gift-form-container" :class="{ 'is-uploading': loading }">
        <el-form ref="formRef" :model="formData" label-width="120px" :rules="rules">
            <el-form-item label="年月"  prop="datatime">
                <el-date-picker
                    v-model="formData.datatime"
                    type="month"
                    placeholder="选择年月"
                    format="YYYY-MM"
                    value-format="YYYY-MM"
                    style="width: 100%">
                </el-date-picker>
            </el-form-item>

            <el-form-item label="标题" prop="title">
                <el-input v-model="formData.title" placeholder="请输入标题"></el-input>
            </el-form-item>

            <el-form-item label="内容" prop="content">
                <el-input type="textarea" v-model="formData.content" :rows="4" placeholder="请输入内容"></el-input>
            </el-form-item>

            <el-form-item label="舰长填写地址" prop="address_1">
                <el-input v-model="formData.address_1" placeholder="请输入舰长表单链接"></el-input>
            </el-form-item>

            <el-form-item label="提督填写地址" prop="address_2">
                <el-input v-model="formData.address_2" placeholder="请输入提督表单链接"></el-input>
            </el-form-item>

            <el-form-item label="总督填写地址" prop="address_3">
                <el-input v-model="formData.address_3" placeholder="请输入总督表单链接"></el-input>
            </el-form-item>

            <el-divider>礼物图片</el-divider>

            <el-form-item v-for="i in 5" :key="i" :label="'礼物图片 ' + i" class="image-upload-item">
                <el-upload 
                    action="#" 
                    :show-file-list="false" 
                    :disabled="loading"
                    :before-upload="(file) => beforeImageUpload(i, file)"
                    accept="image/*">
                    <div v-if="formData[`image_${i}`]" class="image-container">
                        <el-image :src="getImageUrl(formData[`image_${i}`])" fit="cover" class="uploaded-image" />
                        <div class="image-overlay">
                            <el-icon :size="20" @click.stop="handleRemoveImage(i)">
                                <Delete />
                            </el-icon>
                        </div>
                    </div>
                    <el-icon v-else class="upload-icon">
                        <Plus />
                    </el-icon>
                </el-upload>
            </el-form-item>

            <el-form-item>
                <el-button type="primary" @click="submitForm" :loading="loading">提交</el-button>
                <el-button @click="resetForm" :disabled="loading">重置</el-button>
            </el-form-item>
        </el-form>

        <!-- 上传遮罩：转圈动画 + 进度条；挂到 body 上，期间整页点击都被它挡住 -->
        <teleport to="body">
            <div
                v-if="loading"
                class="gift-upload-mask"
                @click.stop.prevent
                @mousedown.stop.prevent
                @mouseup.stop.prevent
                @contextmenu.prevent>
                <div class="gift-upload-card">
                    <div class="gift-upload-spinner"></div>
                    <p class="gift-upload-title">正在上传礼物，请稍候…</p>
                    <el-progress
                        class="gift-upload-progress"
                        :percentage="uploadPercent"
                        :stroke-width="10"
                        :status="uploadPercent >= 100 ? 'success' : ''" />
                    <p class="gift-upload-detail">{{ uploadDetail }}</p>
                    <p class="gift-upload-tip">上传期间请勿关闭或刷新页面</p>
                </div>
            </div>
        </teleport>
    </div>
</template>

<script setup>
import { ref, reactive, computed } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Delete, Plus } from '@element-plus/icons-vue';
import axios from 'axios';

const formRef = ref(null);
const loading = ref(false);
// 上传进度（0-99 表示还在传，100 表示服务器已返回）
const uploadPercent = ref(0);
const uploadedBytes = ref(0);
const totalBytes = ref(0);
const imageMimeTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
// 确保始终返回格式化的日期
const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
};
// 初始化包含所有图片字段的默认对象
const createDefaultFormData = () => ({
    datatime: formatDate(new Date()),
    title: '',
    content: '',
    address_1: '',
    address_2: '',
    address_3: '',
    image_1: null,
    image_2: null,
    image_3: null,
    image_4: null,
    image_5: null
});

const formData = reactive(createDefaultFormData());

const rules = reactive({
    datatime: [
        { required: true, message: '请输入日期', trigger: 'blur' }
    ],
    title: [
        { required: true, message: '请输入标题', trigger: 'blur' },
        { min: 2, max: 50, message: '标题长度在 2 到 50 个字符', trigger: 'blur' }
    ],
    content: [
        { required: true, message: '请输入内容', trigger: 'blur' }
    ]
});

// 字节数转可读文本
const formatSize = (bytes) => {
    const value = Number(bytes) || 0;
    if (value < 1024) return `${value} B`;
    if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
    return `${(value / 1024 / 1024).toFixed(2)} MB`;
};

// 遮罩里的第二行说明
const uploadDetail = computed(() => {
    if (!loading.value) return '';
    if (uploadPercent.value >= 100) return '上传完成';
    if (uploadPercent.value >= 99) return '文件已发送完毕，服务器处理中…';
    if (totalBytes.value > 0) {
        return `已上传 ${formatSize(uploadedBytes.value)} / ${formatSize(totalBytes.value)}`;
    }
    return '正在发送数据…';
});

// axios 上传进度回调
const handleUploadProgress = (event) => {
    const loaded = Number(event?.loaded) || 0;
    const total = Number(event?.total) || 0;
    uploadedBytes.value = loaded;
    if (total > 0) {
        totalBytes.value = total;
        // 留 1% 给「服务器处理」阶段，避免进度到 100% 却还在转圈
        uploadPercent.value = Math.min(99, Math.floor((loaded / total) * 100));
    }
    else {
        // 拿不到总大小时退化成逐格推进，至少让人看到还在动
        uploadPercent.value = Math.min(99, uploadPercent.value + 1);
    }
};

// 统一处理图片URL获取
const getImageUrl = (path) => {
    if (!path) return '';
    return path instanceof File ? URL.createObjectURL(path) : path;
};

// 图片上传处理
const beforeImageUpload = (index, file) => {
    if (loading.value) return false; // 上传中不许再选图
    if (!imageMimeTypes.includes(file.type)) {
        ElMessage.error('只能上传图片文件 (JPG, PNG, GIF, WEBP)');
        return false;
    }
    if (file.size > 10 * 1024 * 1024) {
        ElMessage.error('图片大小不能超过10MB');
        return false;
    }
    formData[`image_${index}`] = file;
    return false;
};

// 图片删除处理
const handleRemoveImage = (index) => {
    if (loading.value) return; // 上传中不许改表单
    ElMessageBox.confirm('确定要删除这张图片吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
    }).then(() => {
        formData[`image_${index}`] = null; // 设置为null而不是删除字段
        ElMessage.success('图片已移除');
    }).catch(() => {});
};
const submitForm = async () => {
    // 上传中重复点击直接忽略，不会有任何反应
    if (loading.value) return;

    try {
        await formRef.value.validate();
    } catch (invalidFields) {
        // 校验不通过：由 el-form 自己在字段下方提示，这里不弹「操作失败」
        return;
    }

    const formDataObj = new FormData();

    // 确保所有字段都被包含，特别是image_1到image_5
    Object.keys(formData).forEach(key => {
        // 对于图片字段，确保1-5全部包含
        if (key.startsWith('image_')) {
            // 如果是文件对象，直接添加
            if (formData[key] instanceof File) {
                formDataObj.append(key, formData[key]);
            } 
            // 如果是字符串路径（已有图片）
            else if (typeof formData[key] === 'string' && formData[key]) {
                formDataObj.append(key, formData[key]);
            }
            // 如果是null或undefined，显式添加空值
            else {
                formDataObj.append(key, '');
            }
        }
        // 非图片字段
        else {
            formDataObj.append(key, formData[key] || '');
        }
    });

    // 进入上传中：显示遮罩 + 转圈 + 进度，整页点击被挡
    loading.value = true;
    uploadPercent.value = 0;
    uploadedBytes.value = 0;
    totalBytes.value = 0;

    try {
        await axios.post('/adminapi/gifts/add', formDataObj, {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: handleUploadProgress
        });

        uploadPercent.value = 100;
        // 让 100% 停留一下再收起遮罩
        await new Promise(resolve => setTimeout(resolve, 400));
        loading.value = false;
        ElMessage.success('礼物添加成功');
        resetForm();
    } catch (error) {
        console.error('提交失败:', error);
        // 先收起遮罩再弹提示，避免提示压在遮罩下面
        loading.value = false;
        if (error?.response) {
            ElMessage.error(error.response.data?.message || '操作失败');
        }
        else {
            ElMessage.error('上传失败，请检查网络后重试');
        }
    }
};

// 重置表单时确保所有图片字段都存在
const resetForm = () => {
    if (loading.value) return; // 上传中不许重置
    Object.assign(formData, createDefaultFormData());
    formRef.value?.resetFields();
};
</script>

<style scoped>
.gift-form-container {
    max-width: 800px;
    margin: 0 auto;
    padding: 20px;
}

/* 上传中：表单整体不可点，配合遮罩双保险 */
.gift-form-container.is-uploading .el-form {
    pointer-events: none;
}

.gift-upload-mask {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 3000;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.45);
    cursor: wait;
    user-select: none;
}

.gift-upload-card {
    width: 320px;
    padding: 28px 24px 18px;
    border-radius: 12px;
    background-color: #fff;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
    text-align: center;
}

.gift-upload-spinner {
    width: 46px;
    height: 46px;
    margin: 0 auto 16px;
    border: 4px solid #e4e7ed;
    border-top-color: #409eff;
    border-radius: 50%;
    animation: gift-upload-spin 0.8s linear infinite;
}

@keyframes gift-upload-spin {
    to {
        transform: rotate(360deg);
    }
}

.gift-upload-title {
    margin: 0 0 16px;
    font-size: 15px;
    font-weight: 500;
    color: #303133;
}

.gift-upload-progress {
    margin-bottom: 10px;
}

.gift-upload-detail {
    margin: 0;
    font-size: 13px;
    color: #606266;
    min-height: 18px;
}

.gift-upload-tip {
    margin: 6px 0 0;
    font-size: 12px;
    color: #909399;
}

.image-upload-item {
    margin-bottom: 20px;
}

.upload-icon {
    font-size: 28px;
    color: #8c939d;
    width: 120px;
    height: 120px;
    line-height: 120px;
    text-align: center;
    border: 1px dashed #d9d9d9;
    border-radius: 6px;
    cursor: pointer;
    transition: border-color 0.3s;
}

.upload-icon:hover {
    border-color: #409eff;
}

.image-container {
    position: relative;
    width: 120px;
    height: 120px;
    overflow: hidden;
    border-radius: 6px;
}

.uploaded-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.image-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s;
    cursor: pointer;
}

.image-container:hover .image-overlay {
    opacity: 1;
}

.image-overlay .el-icon {
    color: white;
    font-size: 20px;
}
</style>
